import React, { useEffect, useState } from 'react';
import { Card, Col, Row, Select, Space, Statistic, Table, Tag, Typography, Spin, Alert, Empty } from 'antd';
import ReactECharts from 'echarts-for-react';
import {
  fetchTriageActivity,
  fetchTimeToTriage,
  fetchUsageSummary,
  fetchUsageDisagreement,
  type TriageActivityResponse,
  type TimeToTriageRow,
  type UsageSummary,
  type UsageDisagreement,
} from '../config/api';

const { Title, Text } = Typography;

const WINDOW_OPTIONS = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
];

// ms → compact human duration (m / h / d).
function msToHuman(ms: number | null | undefined): string {
  if (ms == null) return '—';
  const h = ms / 3_600_000;
  if (h < 1) return `${Math.round(ms / 60_000)}m`;
  if (h < 48) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
}

const UsagePage: React.FC = () => {
  const [windowDays, setWindowDays] = useState<number>(30);
  const [activity, setActivity] = useState<TriageActivityResponse | null>(null);
  const [ttt, setTtt] = useState<TimeToTriageRow[]>([]);
  const [summary, setSummary] = useState<UsageSummary | null>(null);
  const [disagree, setDisagree] = useState<UsageDisagreement | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      fetchTriageActivity(windowDays),
      fetchTimeToTriage(windowDays),
      fetchUsageSummary(),
      fetchUsageDisagreement(windowDays),
    ])
      .then(([a, t, s, d]) => {
        if (cancelled) return;
        setActivity(a);
        setTtt(t.rows);
        setSummary(s);
        setDisagree(d);
      })
      .catch((e) => !cancelled && setError(e.message || 'Failed to load usage metrics'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [windowDays]);

  // Latest day with a time-to-triage sample, for the headline tile.
  const latestTtt = [...ttt].reverse().find((r) => r.p50_ms != null);

  const triagedPerDayOption = {
    tooltip: { trigger: 'axis' },
    legend: { data: ['Operator', 'Auto-closed'], bottom: 0 },
    grid: { left: 40, right: 12, top: 16, bottom: 40 },
    xAxis: { type: 'category', data: (activity?.by_day ?? []).map((d) => d.day.slice(5)) },
    yAxis: { type: 'value', minInterval: 1 },
    series: [
      { name: 'Operator', type: 'bar', stack: 'x', color: '#006EB7', data: (activity?.by_day ?? []).map((d) => d.operator) },
      { name: 'Auto-closed', type: 'bar', stack: 'x', color: '#bfbfbf', data: (activity?.by_day ?? []).map((d) => d.auto) },
    ],
  };

  const tttOption = {
    tooltip: {
      trigger: 'axis',
      formatter: (ps: any[]) =>
        ps[0].axisValue + '<br/>' + ps.map((p) => `${p.marker}${p.seriesName}: ${p.value == null ? '—' : p.value + 'h'}`).join('<br/>'),
    },
    legend: { data: ['p50', 'p90'], bottom: 0 },
    grid: { left: 40, right: 12, top: 16, bottom: 40 },
    xAxis: { type: 'category', data: ttt.map((r) => r.day.slice(5)) },
    yAxis: { type: 'value', name: 'hours' },
    series: [
      { name: 'p50', type: 'line', smooth: true, color: '#389e0d', data: ttt.map((r) => (r.p50_ms == null ? null : +(r.p50_ms / 3_600_000).toFixed(1))) },
      { name: 'p90', type: 'line', smooth: true, color: '#d48806', data: ttt.map((r) => (r.p90_ms == null ? null : +(r.p90_ms / 3_600_000).toFixed(1))) },
    ],
  };

  const overrideTrendOption = {
    tooltip: { trigger: 'axis' },
    legend: { data: ['Labeled', 'Overridden'], bottom: 0 },
    grid: { left: 36, right: 12, top: 16, bottom: 40 },
    xAxis: { type: 'category', data: (disagree?.by_day ?? []).map((d) => d.day.slice(5)) },
    yAxis: { type: 'value', minInterval: 1 },
    series: [
      { name: 'Labeled', type: 'bar', color: '#d9d9d9', data: (disagree?.by_day ?? []).map((d) => d.labeled) },
      { name: 'Overridden', type: 'bar', color: '#cf1322', data: (disagree?.by_day ?? []).map((d) => d.overridden) },
    ],
  };

  const overrideCols = [
    {
      title: 'Heuristic said', dataIndex: 'from_class', key: 'from',
      render: (v: string | null, r: any) => <Tag color="blue">{v || `cls ${r.from_class_id}`}</Tag>,
    },
    {
      title: 'Operator chose', dataIndex: 'to_class', key: 'to',
      render: (v: string | null, r: any) => <Tag color="green">{v || `cls ${r.to_class_id}`}</Tag>,
    },
    { title: 'Count', dataIndex: 'n', key: 'n', align: 'right' as const, width: 90 },
  ];

  return (
    <div style={{ padding: 16, overflow: 'auto', width: '100%' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }} align="center">
        <Title level={4} style={{ margin: 0 }}>Usage &amp; Metrics</Title>
        <Select
          value={windowDays}
          onChange={setWindowDays}
          options={WINDOW_OPTIONS}
          style={{ width: 150 }}
          size="small"
        />
      </Space>

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}

      <Spin spinning={loading}>
        {/* Headline tiles */}
        <Row gutter={[12, 12]}>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic
                title={`Triaged by operators (${windowDays}d)`}
                value={activity?.totals.operator ?? 0}
                suffix={
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {activity && activity.by_day.length
                      ? `≈${Math.round(activity.totals.operator / activity.by_day.length)}/day`
                      : ''}
                  </Text>
                }
              />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic
                title="Time to triage (latest day)"
                value={msToHuman(latestTtt?.p50_ms)}
                suffix={<Text type="secondary" style={{ fontSize: 12 }}>p50 · p90 {msToHuman(latestTtt?.p90_ms)}</Text>}
              />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic
                title={`Overrode heuristic (${windowDays}d)`}
                value={disagree?.override_rate_pct ?? 0}
                suffix="%"
                valueStyle={{ color: (disagree?.override_rate_pct ?? 0) >= 30 ? '#cf1322' : undefined }}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {disagree ? `${disagree.overridden} of ${disagree.total_labeled} labeled` : ''}
              </Text>
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="Open issues" value={summary?.open_total ?? 0} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {summary ? `${summary.suggested_open} suggested · ${summary.p1_open} P1` : ''}
              </Text>
            </Card>
          </Col>
        </Row>

        {/* Ready-to-close (two distinct gauges) */}
        <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="Ready to close · triaged, awaiting close" value={summary?.confirmed_open ?? 0} valueStyle={{ color: '#d48806' }} />
              <Text type="secondary" style={{ fontSize: 12 }}>confirmed issues still open</Text>
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="Ready to close · alerts resolved" value={summary?.alerts_resolved_open ?? 0} valueStyle={{ color: '#389e0d' }} />
              <Text type="secondary" style={{ fontSize: 12 }}>node recovered; sweeper auto-closes ≤15 min</Text>
            </Card>
          </Col>
        </Row>

        {/* Charts */}
        <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
          <Col xs={24} lg={12}>
            <Card size="small" title="Triaged per day">
              {activity?.by_day.length ? <ReactECharts option={triagedPerDayOption} style={{ height: 260 }} /> : <Empty description="No activity in window" />}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card size="small" title="Time to triage (p50 / p90)">
              {ttt.some((r) => r.p50_ms != null) ? <ReactECharts option={tttOption} style={{ height: 260 }} /> : <Empty description="No triaged issues in window" />}
            </Card>
          </Col>
        </Row>

        {/* Manual vs heuristic disagreement */}
        <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
          <Col xs={24} lg={12}>
            <Card size="small" title="Manual vs heuristic — overrides per day">
              {disagree?.by_day.length ? <ReactECharts option={overrideTrendOption} style={{ height: 260 }} /> : <Empty description="No operator labels in window" />}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card size="small" title="Top overrides (heuristic → operator)">
              <Table
                size="small"
                rowKey={(r) => `${r.from_class_id}-${r.to_class_id}`}
                dataSource={disagree?.top_overrides ?? []}
                columns={overrideCols}
                pagination={false}
                locale={{ emptyText: 'No overrides in window' }}
              />
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  );
};

export default UsagePage;
